// Which copy of a member's genotypes is the freshest one on the client.
// TGCLOUD-1837.
//
// ── THE PROBLEM ───────────────────────────────────────────────────────────
// `GET /genotypes` lazily backfills ClinVar provenance onto a variant and
// persists it with entity.save(). The response carries the enriched values, but
// the sidebar's `data` prop does not: it is independent state in ChartBox, set
// once when a node is selected (`setData(member)`), and nothing re-derives it
// from a later fetch.
//
// So three copies could disagree at once — the database had the backfilled
// `clinvarDatasetVersion`, the genotype list on screen showed it because it
// renders the fetch response directly, and the letter engine did not, because
// ContactForm builds its merge context from `data`. After TGCLOUD-1832 that
// surfaced as a letter printing "(ClinVar dataset version not recorded)" for a
// variant whose version had been backfilled and saved moments earlier.
//
// Intermittently wrong is worse than consistently absent: it is harder to
// notice and harder to trust.
//
// ── THE RULE ──────────────────────────────────────────────────────────────
// `memberRecords` is the shared, refreshed copy; `data` is the snapshot taken
// at selection. Where both describe the same person, the genotypes come from
// memberRecords. This is the same lesson already recorded about this sidebar:
// bind to memberRecords, not to a stale `data`.
//
// ONLY `genotypes` is taken. Everything else on `data` — identity, dates,
// diagnoses, the record address — is left exactly as it was, because this is
// about one field that one endpoint refreshes behind the user's back, not about
// deciding which object is authoritative in general.

/**
 * `data` with the freshest genotypes the client holds.
 *
 * @param {object|null} data the sidebar's selected-member snapshot
 * @param {Array<object>} memberRecords the shared member list
 * @returns {object|null} `data` itself when there is nothing fresher, so a
 *   consumer's memo does not invalidate on every render
 */
export function withFreshestGenotypes(data, memberRecords) {
  if (!data || !Array.isArray(memberRecords) || memberRecords.length === 0) {
    return data;
  }
  // `pid` is the in-memory node identity the sidebar already matches on, and it
  // exists for unsaved members too, unlike familyMemberId.
  if (data.pid === undefined || data.pid === null) return data;

  const fresh = memberRecords.find((m) => m && m.pid === data.pid);
  if (!fresh || !Array.isArray(fresh.genotypes)) return data;
  if (fresh.genotypes === data.genotypes) return data;

  return { ...data, genotypes: fresh.genotypes };
}

/**
 * Has the server handed back genotypes that differ from the shared copy?
 *
 * Used to decide whether a READ should write to shared state at all. A fetch
 * that changed nothing must not, or every sidebar open would churn state for
 * no reason.
 */
export function genotypesDiffer(a, b) {
  const left = Array.isArray(a) ? a : [];
  const right = Array.isArray(b) ? b : [];
  if (left.length !== right.length) return true;
  // The backfill only ever adds or changes scalar provenance fields, so a
  // structural comparison is enough and avoids a field allowlist that would
  // rot the next time a provenance field is added.
  return JSON.stringify(left) !== JSON.stringify(right);
}
